import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const DesignUXUI = () => {
  const services = [
    {
      num: "01",
      title: "Audit UX",
      description: "Analyse approfondie de vos interfaces pour identifier les points de friction.",
      features: ["Analyse heuristique", "Tests utilisateurs", "Recommandations d'amélioration"],
    },
    {
      num: "02",
      title: "Prototypage Interactif",
      description: "Création de prototypes fonctionnels pour valider vos concepts.",
      features: ["Wireframes & Maquettes", "Prototypes cliquables", "Tests A/B"],
    },
    {
      num: "03",
      title: "Design System",
      description: "Construction d'une bibliothèque de composants cohérente et réutilisable.",
      features: ["Bibliothèque de composants", "Guide de style", "Documentation complète"],
    },
    {
      num: "04",
      title: "Tests Utilisateurs",
      description: "Validation de vos interfaces avec de vrais utilisateurs.",
      features: ["Sessions de test modérées", "Analyse comportementale", "Rapport d'insights"],
    },
  ];

  return (
    <div className="bg-gray-950 text-white min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-end pb-20">
        <div className="absolute inset-0">
          <img
            src="/images/design-ui/2149930986.jpg"
            alt="Design UX/UI"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/70 to-gray-950/30" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 w-full">
          <div className="max-w-3xl">
            <div className="inline-block px-4 py-1 border border-flowkraft-primary/30 text-flowkraft-secondary text-sm mb-6">
              Services
            </div>
            <h1 className="font-display text-display text-white mb-6">
              Design UX/UI
            </h1>
            <p className="text-xl text-gray-300 max-w-xl">
              Conception d'expériences utilisateur innovantes et interfaces intuitives
            </p>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-20 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-display text-title text-white mb-6">
                Design Centré Utilisateur
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-6">
                Nous créons des expériences digitales qui captivent vos utilisateurs
                et boostent vos conversions. Notre approche allie recherche
                utilisateur, design thinking et prototypage itératif.
              </p>
              <p className="text-gray-400 text-lg leading-relaxed">
                Chaque pixel compte. Chaque interaction est pensée pour maximiser
                l'engagement et la satisfaction de vos utilisateurs.
              </p>
            </div>
            <div className="relative">
              <img
                src="/images/design-ui/167109.jpg"
                alt="UX/UI Design"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute inset-0 border border-white/10" />
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-title text-white mb-16">
            Nos Services Design
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="border border-white/10 p-8 hover:border-flowkraft-primary/50 transition-colors"
              >
                <span className="font-display text-4xl text-flowkraft-primary/30 mb-4 block">
                  {service.num}
                </span>
                <h3 className="font-display text-2xl text-white mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-400 mb-6">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center text-gray-300 text-sm">
                      <Check className="w-4 h-4 text-flowkraft-secondary mr-3" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Showcase */}
      <section className="py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="relative aspect-square overflow-hidden">
              <img
                src="/images/design-ui/038904.jpg"
                alt="Design Work"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 border border-white/10" />
            </div>
            <div className="relative aspect-square overflow-hidden">
              <img
                src="/images/design-ui/104489.jpg"
                alt="Design Work"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 border border-white/10" />
            </div>
            <div className="relative aspect-square overflow-hidden">
              <img
                src="/images/design-ui/215003.jpg"
                alt="Design Work"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 border border-white/10" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative">
        <div className="absolute inset-0 opacity-10">
          <img
            src="/images/main/cta_background_digital.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 text-center">
          <h2 className="font-display text-title text-white mb-6">
            Créons ensemble une expérience mémorable
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
            Transformez vos interfaces en expériences captivantes
          </p>
          <Link
            to="/#contact"
            className="btn-solid inline-flex"
          >
            Discuter de votre projet
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DesignUXUI;
